import { motion } from "framer-motion";
import { MapPin, FlaskConical, ArrowRight, MessageCircle, BadgeCheck } from "lucide-react";
import { STORE, SECTORS, waLink } from "../data/store";

export const Hero = ({ stats, onNav }) => {
  return (
    <section className="relative overflow-hidden paint-drip-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-14 lg:pt-16 lg:pb-20">
        <div className="grid lg:grid-cols-12 gap-8 items-stretch">
          {/* Left - headline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-7 flex flex-col justify-center"
          >
            <div className="flex flex-wrap gap-2 mb-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0A3977] text-white text-xs font-bold tracking-wide">
                <MapPin className="w-3.5 h-3.5" /> Hernandarias · Alto Paraná
              </div>
              <div data-testid="hero-sw-badge" className="inline-flex items-center gap-2 pl-3 pr-1.5 py-1 rounded-full bg-[#E02424] text-white text-xs font-bold tracking-wide">
                <BadgeCheck className="w-3.5 h-3.5" /> Distribuidor autorizado
                <span className="inline-flex items-center bg-white rounded-full px-2 py-0.5"><img src="/brands/sherwin-williams.png" alt="Sherwin-Williams" className="h-4 w-auto" /></span>
              </div>
            </div>
            <div data-testid="hero-slogan" className="font-display text-sm sm:text-base font-bold tracking-widest uppercase text-[#0067B1] mb-2">
              {STORE.name}, {STORE.slogan.toLowerCase()}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] text-gray-900">
              El color exacto para tu{" "}
              <span className="text-[#0067B1]">hogar</span>,{" "}
              <span className="text-[#E02424]">taller</span> o{" "}
              <span className="text-[#D97706]">industria</span>.
            </h1>
            <p className="mt-5 text-lg text-gray-600 max-w-xl">
              Pinturas inmobiliarias, automotivas e industriales. Accesorios e insumos para
              chapería y pintura. Marcas <strong>Sherwin-Williams</strong> y <strong>Killing</strong>.
              Te asesoramos para renovar tu casa, tu auto o tu empresa.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                data-testid="hero-ver-catalogo"
                onClick={() => onNav("inmobiliario")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E02424] text-white font-bold hover:bg-[#c01f1f] transition-colors"
              >
                Ver catálogo <ArrowRight className="w-4 h-4" />
              </button>
              <a
                data-testid="hero-whatsapp"
                href={waLink(`Hola ${STORE.name}, quiero hacer una consulta.`)}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] text-white font-bold hover:bg-[#1EBE5B] transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Consultar por WhatsApp
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {SECTORS.map((s) => (
                <button
                  key={s.key}
                  data-testid={`hero-pill-${s.key}`}
                  onClick={() => onNav(s.key)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border transition-transform hover:-translate-y-0.5"
                  style={{ background: s.bg, color: s.text, borderColor: s.border }}
                >
                  {s.label}
                  <span className="font-mono-code text-xs opacity-70">{stats?.[s.key] ?? "·"}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Right - lab card + image */}
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 grid grid-rows-5 gap-4"
          >
            <div className="row-span-3 rounded-2xl overflow-hidden relative shadow-lg border border-gray-200 min-h-[260px]">
              <img
                src="https://images.unsplash.com/photo-1773525911720-e1aaacd0af31?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                alt="Estantes de pintura" className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <div className="text-xs font-bold tracking-widest uppercase opacity-80">Tienda física</div>
                <div className="font-display font-bold text-lg">{STORE.address}</div>
              </div>
            </div>

            <button
              onClick={() => onNav("lab")}
              data-testid="hero-lab-card"
              className="row-span-2 group text-left rounded-2xl p-5 bg-gradient-to-br from-[#0A3977] to-[#0067B1] text-white shadow-lg relative overflow-hidden"
            >
              <FlaskConical className="absolute -right-3 -bottom-3 w-28 h-28 text-white/10" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/15 text-[11px] font-bold tracking-wide mb-2">
                  <FlaskConical className="w-3.5 h-3.5" /> Laboratorio de Color
                </div>
                <h3 className="font-display font-bold text-xl leading-snug">
                  Preparamos tu color computarizado y personalizado
                </h3>
                <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#F59E0B] group-hover:gap-2 transition-all">
                  Probar el mezclador <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
